import { loginWithCredentials } from '@/features/auth';
import { refreshAuthMe } from '@/features/user/api/user.cache';
import { ISignIn } from '@/shared';

export type CompleteAuthenticatedUserFlowArgs = {
  redirectUrl?: string | null;
  skipHrRedirect?: boolean;
  onSuccessAuthModal?: () => void;
  loginType?: string;
};

export type CompletePasswordAuthFlowArgs = CompleteAuthenticatedUserFlowArgs & {
  credentials: ISignIn;
};

const normalizeRedirectUrl = (redirectUrl: string) => {
  if (redirectUrl.startsWith('/')) {
    return redirectUrl;
  }

  if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
    return redirectUrl;
  }

  return `https://${redirectUrl}`;
};

function isOAuthRedirect(redirectUrl?: string | null): boolean {
  if (!redirectUrl) {
    return false;
  }

  return (
    redirectUrl.startsWith('/oauth/') ||
    redirectUrl.includes('/oauth/authorize')
  );
}

export const completeAuthenticatedUserFlow = async ({
  redirectUrl,
  skipHrRedirect = false,
  onSuccessAuthModal,
  loginType,
}: CompleteAuthenticatedUserFlowArgs) => {
  const user = await refreshAuthMe();
  const hasOwnedCompany = (user?.ownedCompanies ?? []).length > 0;
  const shouldGoToHrSite = loginType === 'hr' || hasOwnedCompany;

  if (
    !skipHrRedirect &&
    !isOAuthRedirect(redirectUrl) &&
    shouldGoToHrSite &&
    process.env.NEXT_PUBLIC_APP_HR_SITE_URL
  ) {
    window.location.href = process.env.NEXT_PUBLIC_APP_HR_SITE_URL;
    return { redirected: true, user };
  }

  if (redirectUrl) {
    if (redirectUrl.startsWith('/')) {
      window.location.href = redirectUrl;
      return { redirected: true, user };
    }

    const href = normalizeRedirectUrl(redirectUrl);

    try {
      const { hostname } = new URL(href);
      const domainParts = hostname.split('.');
      const subdomain = domainParts.length > 2 ? domainParts[0] : '';

      if (subdomain === 'hr' && !hasOwnedCompany) {
        window.location.href = '/dashboard';
      } else {
        window.location.href = href;
      }

      return { redirected: true, user };
    } catch {
      window.location.href = '/dashboard';
      return { redirected: true, user };
    }
  }

  onSuccessAuthModal?.();
  return { redirected: false, user };
};

export const completePasswordAuthFlow = async ({
  credentials,
  redirectUrl,
  skipHrRedirect = false,
  onSuccessAuthModal,
}: CompletePasswordAuthFlowArgs) => {
  await loginWithCredentials(credentials);

  return completeAuthenticatedUserFlow({
    redirectUrl,
    skipHrRedirect,
    onSuccessAuthModal,
    loginType: credentials.type,
  });
};
